import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, ShieldCheck, ShieldAlert, Activity, LogIn, XCircle, KeyRound, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/security`;

const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const SecurityTab = () => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ event: '', outcome: '' });
  const [totp, setTotp] = useState({ enabled: false, loading: false });
  const [setupData, setSetupData] = useState(null); // {secret, otpauth_uri, qr_svg_data_url}
  const [totpCode, setTotpCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [disableOpen, setDisableOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l, st] = await Promise.all([
        axios.get(`${API}/audit-log/summary`, { headers: authHeader() }),
        axios.get(`${API}/audit-log`, { params: { ...filter, limit: 50 }, headers: authHeader() }),
        axios.get(`${API}/2fa/status`, { headers: authHeader() }),
      ]);
      setSummary(s.data);
      setItems(l.data.items || []);
      setTotp({ enabled: st.data.enabled, loading: false });
    } catch (e) {
      toast.error(t('admin.security.loadError'));
    } finally {
      setLoading(false);
    }
  }, [filter, t]);

  useEffect(() => { load(); }, [load]);

  const applyFilter = () => load();

  const startSetup = async () => {
    try {
      const r = await axios.post(`${API}/2fa/setup`, {}, { headers: authHeader() });
      setSetupData(r.data);
      setTotpCode('');
    } catch (e) { toast.error(t('admin.common.saveError')); }
  };

  const confirmEnable = async () => {
    try {
      const r = await axios.post(`${API}/2fa/enable`, { code: totpCode }, { headers: authHeader() });
      setBackupCodes(r.data.backup_codes || []);
      setSetupData(null);
      toast.success(t('admin.security.twoFaEnabled'));
      setTotp({ enabled: true, loading: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('admin.common.saveError'));
    }
  };

  const confirmDisable = async () => {
    try {
      await axios.post(`${API}/2fa/disable`, { code: totpCode }, { headers: authHeader() });
      toast.success('2FA deaktiviert');
      setTotp({ enabled: false, loading: false });
      setDisableOpen(false);
      setTotpCode('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Code ungültig');
    }
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(items, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const eventLabel = (e) => ({
    'auth.login.success': ['Login erfolgreich', 'bg-green-100 text-green-800'],
    'auth.login.failed': ['Login fehlgeschlagen', 'bg-red-100 text-red-800'],
    'auth.register': ['Registrierung', 'bg-blue-100 text-blue-800'],
    'auth.password.change': ['Passwort geändert', 'bg-amber-100 text-amber-800'],
    'auth.2fa.enabled': ['2FA aktiviert', 'bg-emerald-100 text-emerald-800'],
    'auth.2fa.disabled': ['2FA deaktiviert', 'bg-orange-100 text-orange-800'],
    'auth.2fa.verified': ['2FA verifiziert', 'bg-green-100 text-green-800'],
    'auth.2fa.failed': ['2FA falsch', 'bg-red-100 text-red-800'],
    'security.rate_limit.exceeded': ['Rate-Limit', 'bg-purple-100 text-purple-800'],
  }[e] || [e, 'bg-gray-100 text-gray-800']);

  return (
    <div className="space-y-6" data-testid="security-tab">
      {/* 2FA Status Card */}
      <Card data-testid="security-2fa-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            {totp.enabled ? <ShieldCheck className="h-5 w-5 text-emerald-600" /> : <ShieldAlert className="h-5 w-5 text-amber-600" />}
            Zwei-Faktor-Authentifizierung (TOTP)
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm text-muted-foreground">
            {totp.enabled
              ? 'Dein Account ist mit einem TOTP-Authenticator geschützt (ISO 27001 A.8.5 / A.9.4.2).'
              : 'Empfohlen für alle Admin-Accounts — erhöht den Schutz gegen Credential-Theft erheblich.'}
          </div>
          {totp.enabled
            ? <Button variant="outline" size="sm" onClick={() => setDisableOpen(true)} data-testid="disable-2fa-btn">2FA deaktivieren</Button>
            : <Button size="sm" onClick={startSetup} data-testid="enable-2fa-btn"><KeyRound className="h-4 w-4 mr-1" />2FA einrichten</Button>}
        </CardContent>
      </Card>

      {/* Summary widgets */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card data-testid="sec-stat-events">
          <CardContent className="pt-6 flex items-center justify-between">
            <div>
              <div className="text-sm text-muted-foreground">Ereignisse letzte 7 Tage</div>
              <div className="text-3xl font-bold">{loading ? '…' : (summary?.total_events_7d ?? 0)}</div>
            </div>
            <Activity className="h-8 w-8 text-blue-500" />
          </CardContent>
        </Card>
        <Card data-testid="sec-stat-failed-logins">
          <CardContent className="pt-6 flex items-center justify-between">
            <div>
              <div className="text-sm text-muted-foreground">Fehlgeschlagene Logins 7T</div>
              <div className="text-3xl font-bold text-red-600">{loading ? '…' : (summary?.failed_logins_7d ?? 0)}</div>
            </div>
            <XCircle className="h-8 w-8 text-red-500" />
          </CardContent>
        </Card>
        <Card data-testid="sec-stat-compliance">
          <CardContent className="pt-6 flex items-center justify-between">
            <div>
              <div className="text-sm text-muted-foreground">ISO 27001 Compliance</div>
              <div className="text-sm text-emerald-700 font-semibold mt-1">Audit-Log aktiv</div>
              <div className="text-xs text-muted-foreground mt-0.5">A.8.15 Logging</div>
            </div>
            <Shield className="h-8 w-8 text-emerald-500" />
          </CardContent>
        </Card>
      </div>

      {/* Audit-Log Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2"><FileText className="h-5 w-5" />Audit-Log</CardTitle>
          <Button variant="outline" size="sm" onClick={exportJson} data-testid="audit-export-btn">
            <Download className="h-4 w-4 mr-1" />Export JSON
          </Button>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            <Input placeholder="Event-Filter (z. B. auth.login.failed)" value={filter.event}
                   onChange={(e) => setFilter({ ...filter, event: e.target.value })}
                   className="max-w-xs" data-testid="filter-event-input" />
            <select value={filter.outcome}
                    onChange={(e) => setFilter({ ...filter, outcome: e.target.value })}
                    className="border rounded-md px-2 text-sm" data-testid="filter-outcome-select">
              <option value="">{t('admin.security.allOutcomes')}</option>
              <option value="success">success</option>
              <option value="failure">failure</option>
              <option value="blocked">blocked</option>
            </select>
            <Button size="sm" onClick={applyFilter} data-testid="apply-filter-btn">Filtern</Button>
            <Button size="sm" variant="ghost" onClick={() => { setFilter({ event: '', outcome: '' }); load(); }}>Reset</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-muted-foreground border-b">
                <th className="py-2 pr-2">Zeit</th>
                <th className="py-2 pr-2">Ereignis</th>
                <th className="py-2 pr-2">User</th>
                <th className="py-2 pr-2">IP</th>
                <th className="py-2 pr-2">Outcome</th>
              </tr></thead>
              <tbody data-testid="audit-log-rows">
                {loading ? (
                  <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Lade…</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">{t('admin.common.noEntries')}</td></tr>
                ) : items.map((it) => {
                  const [label, klass] = eventLabel(it.event);
                  return (
                    <tr key={it.audit_id} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="py-1.5 pr-2 whitespace-nowrap text-muted-foreground">{new Date(it.created_at).toLocaleString('de-DE')}</td>
                      <td className="py-1.5 pr-2"><Badge className={klass}>{label}</Badge></td>
                      <td className="py-1.5 pr-2">{it.user_email || '—'}</td>
                      <td className="py-1.5 pr-2 font-mono text-xs">{it.ip || '—'}</td>
                      <td className="py-1.5 pr-2">
                        <Badge variant={it.outcome === 'success' ? 'default' : 'destructive'}>{it.outcome}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* 2FA Setup Dialog */}
      <Dialog open={!!setupData} onOpenChange={(v) => !v && setSetupData(null)}>
        <DialogContent data-testid="2fa-setup-dialog">
          <DialogHeader>
            <DialogTitle>2FA einrichten</DialogTitle>
            <DialogDescription>{t('admin.security.scanQrHint')}</DialogDescription>
          </DialogHeader>
          {setupData && (
            <div className="space-y-3">
              {setupData.qr_svg_data_url ? (
                <img src={setupData.qr_svg_data_url} alt="QR" className="mx-auto border rounded p-2 bg-white w-48 h-48" />
              ) : (
                <Alert><AlertDescription className="break-all text-xs">{setupData.otpauth_uri}</AlertDescription></Alert>
              )}
              <div>
                <div className="text-xs text-muted-foreground mb-1">Secret (manuell eingeben):</div>
                <code className="block p-2 bg-muted rounded text-xs break-all">{setupData.secret}</code>
              </div>
              <Input placeholder="6-stelliger Code" value={totpCode}
                     onChange={(e) => setTotpCode(e.target.value)} maxLength={6}
                     data-testid="2fa-setup-code-input" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetupData(null)}>Abbrechen</Button>
            <Button onClick={confirmEnable} disabled={totpCode.length < 6} data-testid="2fa-confirm-enable-btn">Aktivieren</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={!!backupCodes} onOpenChange={(v) => !v && setBackupCodes(null)}>
        <DialogContent data-testid="backup-codes-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-600" />2FA aktiv — Backup-Codes sichern</DialogTitle>
            <DialogDescription>{t('admin.security.backupCodesHint')}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm">
            {backupCodes?.map((c) => <div key={c} className="p-2 bg-muted rounded">{c}</div>)}
          </div>
          <DialogFooter>
            <Button onClick={() => setBackupCodes(null)}>Gespeichert</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable 2FA Dialog */}
      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>2FA deaktivieren</DialogTitle>
            <DialogDescription>{t('admin.security.enterCodeHint')}</DialogDescription>
          </DialogHeader>
          <Input placeholder="6-stelliger Code" value={totpCode} onChange={(e) => setTotpCode(e.target.value)} maxLength={6} data-testid="2fa-disable-code-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisableOpen(false)}>Abbrechen</Button>
            <Button variant="destructive" onClick={confirmDisable} data-testid="2fa-confirm-disable-btn">Deaktivieren</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Policy Docs pointer */}
      <Alert>
        <LogIn className="h-4 w-4" />
        <AlertDescription className="text-sm">
          <strong>ISO 27001:</strong> Technische Controls sind aktiv (Audit-Log, 2FA, Rate-Limiting, Security-Headers, Password-Policy, Backup-Script).
          Die vollständigen Richtlinien &amp; Prozess-Templates findest du unter <code>/app/compliance/</code> im Repo.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default SecurityTab;
